import dayjs from 'dayjs';
import dompurify from "dompurify";
import colorWheel from '../Theme/ColorWheel';
import { formatDate, formatTime } from "./Formatters";

export const serializeParams = (params) => {
  const str = [];
  for (const p in params)
    if (params.hasOwnProperty(p)) {
      //If value is an array it must be serialized as key=value1&key=value2...
      if (params[p] && params[p].constructor === Array) {
        const values = params[p];
        for (let i = 0; i < values.length; i++) {
          str.push(encodeURIComponent(p) + "=" + encodeURIComponent(values[i]));
        }
      } else {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(params[p]));
      }
    }
  return str.join("&");
};

//Used for display only. will make sure a given date has the user's browser UTC offset when grabbing a date.
//In order for this to work, the date being grabbed CANNOT have the [JsonConverter(typeof(DateConverter))] attribute on the date field being called.
export const getUTCFormattedDate = (date) => {
  const newDate = new Date(date);
  return formatDate(newDate);
};

// eslint-disable-next-line
export const generateGuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );

export const getCreatedModifiedData = (
  createdOn,
  modifiedOn,
  createdByName,
  modifiedByName,
  userLabels
) => {
  const data =
    <span>Created by {createdByName}
      <span style={{ color: colorWheel.plumPurple, fontWeight: 500 }}>{userLabels} </span> on {formatDate(createdOn)} @ {formatTime(createdOn)}
      {(modifiedOn != null && modifiedOn > createdOn ? ` and Edited by ${modifiedByName} on ${formatDate(modifiedOn)} @ ${formatTime(modifiedOn)}`
        : "")}</span>

  return data;
};

export const getModifiedData = (
  modifiedOn,
  modifiedByName
) => {
  const data =
    (modifiedOn != null
      ? "Edited by " +
      modifiedByName +
      " on " +
      formatDate(modifiedOn) +
      " @ " +
      formatTime(modifiedOn)
      : "");

  return data;
};

export const removeHtml = (source) => {
  const element = document.createElement("div");
  element.innerHTML = dompurify.sanitize(source);
  return element.textContent || element.innerText;
};

//required to distinguish if numbers are empty or zero
export const isEmpty = (val) =>
  val === undefined || val === null || val.length <= 0 ? true : false;

export const setSession = (key, value) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const removeSession = (key) => {
  sessionStorage.removeItem(key);
};

export const filterUnique = (value, index, self) => {
  return self.indexOf(value) === index
}

export const getSession = (key) => {
  const sessionValue = sessionStorage.getItem(key);
  if (sessionValue) return JSON.parse(sessionValue);
  return sessionValue;
};

export const setLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const removeLocalStorage = (key) => {
  localStorage.removeItem(key);
};

export const getLocalStorage = (key) => {
  const localStorageValue = localStorage.getItem(key);
  if (localStorageValue) return JSON.parse(localStorageValue);
  return localStorageValue;
};

/**
 * Compares an array
 * @param arr1
 * @param arr2
 * @returns {boolean}
 */
export const compareArray = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false
  /**
  for (let i = 0, len = arr1.length; i < len; i++){
    if (arr1[i] !== arr2[i]){
      return false
    }
  }**/
  return true
}

/**
 * @desc - allow dates to be filtered by a two-digit year
 * @param string
 * @param match
 * @returns {boolean}
 */
export const compareDates = (string, match) => {
  if (string?.length >= 4) {
    // Check for a two-digit year using regex
    const datePattern = /(\d{1,2})\/(\d{1,2})\/(\d{2})$/;
    const selectedDate = datePattern.exec(string);

    // 4 items in an array for a 2-year digit valid date
    if (selectedDate?.length === 4) {
      const yearField = parseInt(selectedDate[3]);
      const yearFields = ["19" + yearField, "20" + yearField];

      // override dayjs and map the valid date matches
      const dates = yearFields.map((yearField) =>
        dayjs(string).set({ year: yearField }).format("MM/DD/YYYY")
      );

      // return the values that match on
      if (dates && dates.length === 2)
        return dayjs(dates[0]).isSame(match) || dayjs(dates[1]).isSame(match);
    }
  }
  return dayjs(string).isSame(match);
};

export const getValueFromJsonData = (jsonData, dataName) => {

  if (jsonData) {
    let jsonObject = JSON.parse(jsonData);

    let value = jsonObject[dataName];

    return value;
  }
  return null;
}

export const updateJsonDataWithValue = (jsonData, dataName, newValue) => {
  let jsonObject;

  if (jsonData) {
    jsonObject = JSON.parse(jsonData);
  }
  else {
    jsonObject = {}
  }

  jsonObject[dataName] = newValue;
  return JSON.stringify(jsonObject);
}

export const cleanNonSpaceWhiteSpace = (str) => {
  return str.replace(/[\t\n\r\v]/g, "");
};

export const isMinor = (birthdate) => {
  if (birthdate) {
    const mDiff = Date.now() - birthdate.getTime();
    const ageDate = new Date(mDiff);
    const year = ageDate.getUTCFullYear();
    const age = Math.abs(year - 1970);

    return age < 18;
  }
}

export const isUnderGivenAge = (birthdate, givenAge) => {
  if (birthdate) {
    const mDiff = Date.now() - birthdate.getTime();
    const ageDate = new Date(mDiff);
    const year = ageDate.getUTCFullYear();
    const age = Math.abs(year - 1970);

    return age < givenAge;
  }
}

export const doLabelsMatch = (user, associatedLabels) => {
  if (!user.profile.userLabelsEnabled || associatedLabels === null)
    return true;
  else {
    if (!user.profile.userLabel)
      return false;
    else {
      const uLabels = user.profile.userLabel.trim().split(', ');
      const aLabels = associatedLabels.trim().split(', ');

      return uLabels.some(ul => aLabels.includes(ul));
    }
  }
}
